/**
 * ============================================================================
 * Modal - Base Modal Component using Headless UI
 * ============================================================================
 *
 * This is the foundation for all modals in the vendor-admin app.
 * Uses Headless UI Dialog for:
 * - Proper scroll locking (especially iOS Safari)
 * - Focus trapping (accessibility)
 * - ESC key to close (built-in)
 * - Click outside to close (built-in)
 * - Smooth transitions
 *
 * Usage:
 *   <Modal isOpen={isOpen} onClose={onClose} size="md">
 *     <Modal.Header>
 *       <Modal.Title>My Title</Modal.Title>
 *     </Modal.Header>
 *     <Modal.Body>Content here</Modal.Body>
 *     <Modal.Footer>Buttons here</Modal.Footer>
 *   </Modal>
 *
 * Or with custom content:
 *   <Modal isOpen={isOpen} onClose={onClose}>
 *     <div>Completely custom content</div>
 *   </Modal>
 *
 * ============================================================================
 */

import React, { Fragment } from 'react';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { X } from 'lucide-react';

// ============================================================================
// TYPES
// ============================================================================

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full';
type ModalVariant = 'default' | 'lightbox';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: ModalSize;
  /** Modal variant: 'default' for standard modals, 'lightbox' for image viewers */
  variant?: ModalVariant;
  /** Set to true to prevent closing when clicking outside or pressing ESC */
  preventClose?: boolean;
  /** Custom z-index class (default: z-50) */
  zIndex?: string;
  /** Custom max-height class (default: max-h-[90vh]) */
  maxHeight?: string;
  /** Set to false to disable enter/leave animations (default: true) */
  animate?: boolean;
}

interface ModalHeaderProps {
  children: React.ReactNode;
  className?: string;
  /** Custom background color class */
  bgColor?: string;
  /** Show close button (default: true) */
  showClose?: boolean;
  onClose?: () => void;
}

interface ModalTitleProps {
  children: React.ReactNode;
  className?: string;
}

interface ModalBodyProps {
  children: React.ReactNode;
  className?: string;
  /** Add padding (default: true) */
  padded?: boolean;
}

interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
}

// ============================================================================
// SIZE MAPPINGS
// ============================================================================

const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  full: 'max-w-full mx-4',
};

// ============================================================================
// MODAL COMPONENT
// ============================================================================

function Modal({
  isOpen,
  onClose,
  children,
  size = 'lg',
  variant = 'default',
  preventClose = false,
  zIndex = 'z-50',
  maxHeight = 'max-h-[90vh]',
  animate = true,
}: ModalProps) {
  const handleClose = () => {
    if (!preventClose) {
      onClose();
    }
  };

  const isLightbox = variant === 'lightbox';

  // Animation classes - use fast 150ms or instant (no animation)
  const backdropEnter = animate ? "ease-out duration-150" : "";
  const backdropEnterFrom = animate ? "opacity-0" : "";
  const backdropEnterTo = animate ? "opacity-100" : "";
  const backdropLeave = animate ? "ease-in duration-100" : "";
  const backdropLeaveFrom = animate ? "opacity-100" : "";
  const backdropLeaveTo = animate ? "opacity-0" : "";

  const panelEnter = animate ? "ease-out duration-150" : "";
  const panelEnterFrom = animate ? "opacity-0 scale-95" : "";
  const panelEnterTo = animate ? "opacity-100 scale-100" : "";
  const panelLeave = animate ? "ease-in duration-100" : "";
  const panelLeaveFrom = animate ? "opacity-100 scale-100" : "";
  const panelLeaveTo = animate ? "opacity-0 scale-95" : "";

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={handleClose} className={`relative ${zIndex}`} autoFocus>
        {/* Backdrop */}
        <TransitionChild
          as={Fragment}
          enter={backdropEnter}
          enterFrom={backdropEnterFrom}
          enterTo={backdropEnterTo}
          leave={backdropLeave}
          leaveFrom={backdropLeaveFrom}
          leaveTo={backdropLeaveTo}
        >
          <div
            className={`fixed inset-0 ${isLightbox ? 'bg-black/90' : 'bg-black/50'}`}
            aria-hidden="true"
          />
        </TransitionChild>

        {/* Modal container */}
        <div className="fixed inset-0 flex items-center justify-center p-2 sm:p-4">
          <TransitionChild
            as={Fragment}
            enter={panelEnter}
            enterFrom={panelEnterFrom}
            enterTo={panelEnterTo}
            leave={panelLeave}
            leaveFrom={panelLeaveFrom}
            leaveTo={panelLeaveTo}
          >
            <DialogPanel
              className={
                isLightbox
                  ? 'w-full h-full flex items-center justify-center'
                  : `
                      bg-white rounded-2xl shadow-xl w-full
                      ${sizeClasses[size]}
                      ${maxHeight}
                      overflow-hidden flex flex-col
                    `
              }
            >
              {children}
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}

// ============================================================================
// MODAL HEADER
// ============================================================================

function ModalHeader({
  children,
  className = '',
  bgColor = 'bg-white',
  showClose = true,
  onClose,
}: ModalHeaderProps) {
  return (
    <div
      className={`
        sticky top-0 ${bgColor} border-b border-gray-200
        px-4 sm:px-6 py-3 sm:py-4
        flex items-center justify-between
        flex-shrink-0 z-10
        ${className}
      `}
    >
      <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
        {children}
      </div>
      {showClose && onClose && (
        <button
          onClick={onClose}
          className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors flex-shrink-0 ml-2"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>
      )}
    </div>
  );
}

// ============================================================================
// MODAL TITLE
// ============================================================================

function ModalTitle({ children, className = '' }: ModalTitleProps) {
  return (
    <DialogTitle
      className={`text-lg sm:text-xl font-bold text-gray-900 ${className}`}
    >
      {children}
    </DialogTitle>
  );
}

// ============================================================================
// MODAL BODY
// ============================================================================

function ModalBody({
  children,
  className = '',
  padded = true,
}: ModalBodyProps) {
  return (
    <div
      className={`
        flex-1 overflow-y-auto
        ${padded ? 'p-4 sm:p-6' : ''}
        ${className}
      `}
    >
      {children}
    </div>
  );
}

// ============================================================================
// MODAL FOOTER
// ============================================================================

function ModalFooter({ children, className = '' }: ModalFooterProps) {
  return (
    <div
      className={`
        sticky bottom-0 bg-white border-t border-gray-200
        px-4 sm:px-6 py-3 sm:py-4
        flex-shrink-0
        ${className}
      `}
    >
      {children}
    </div>
  );
}

// ============================================================================
// COMPOUND COMPONENT EXPORTS
// ============================================================================

Modal.Header = ModalHeader;
Modal.Title = ModalTitle;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export default Modal;
export { Modal, ModalHeader, ModalTitle, ModalBody, ModalFooter };
export type { ModalProps, ModalSize, ModalVariant };
